import * as React from 'react';
import { observer } from 'mobx-react';
import AlertTable from '~/components/AlertTable';
import { Alert as AlertComp, Button, Panel, Spinner } from '@ixicare/ui';
import { t } from '~/i18n';
import { ExportModal } from './AlertsExportModal';
import { exportAlerts } from './export-alerts';
import { LowBatteryDeviceTable } from './LowBatteryDeviceTable';
import { useLowBatteryDevices } from './utils';
import { useStores } from '~store/utils';
import { useSiteConfiguration, deviceVerificationEnabled } from '~containers/ConfigurationForm/ConfigurationForm';

const AlertTableContainer: React.FC = observer(() => {
  const { alerts: alertsStore, sites } = useStores();
  const selectedSite = sites.selectedSite;
  const selectedSiteUuid = selectedSite?.uuid;

  const { data: siteConfig } = useSiteConfiguration(selectedSiteUuid ?? null);
  const deviceVerification = deviceVerificationEnabled(siteConfig);

  React.useEffect(() => {
    if (!selectedSiteUuid) return;
    alertsStore.fetchAlerts(selectedSiteUuid);
  }, [selectedSiteUuid, alertsStore]);

  const alerts = alertsStore.alertsSorted;
  const openAlerts = React.useMemo(() => alerts.filter((a) => a.isOpen), [alerts]);
  const acceptedAlerts = React.useMemo(() => alerts.filter((a) => a.isAcceptedOwnExcluded), [alerts]);
  const resolvedAlerts = React.useMemo(() => alerts.filter((a) => a.isResolved), [alerts]);

  const [showExportModal, setShowExportModal] = React.useState(false);

  const lowBatteryDevices = useLowBatteryDevices();

  if (alertsStore.isLoading) {
    return <Spinner label={t('global.loading')} />;
  }
  return (
    <>
      <Panel className="mb-25">
        <Panel.Title>{t('alert.status.needsResponse')}</Panel.Title>
        {openAlerts.length ? (
          <AlertTable alerts={openAlerts} />
        ) : (
          <AlertComp type="message">{t('alert.status.noAlerts.needsResponse')}</AlertComp>
        )}
      </Panel>

      <Panel className="mb-25">
        <Panel.Title>{t('alert.status.beingRespondedTo')}</Panel.Title>
        {acceptedAlerts.length ? (
          <AlertTable alerts={acceptedAlerts} />
        ) : (
          <AlertComp type="message">{t('alert.status.noAlerts.beingRespondedTo')}</AlertComp>
        )}
      </Panel>

      {lowBatteryDevices && lowBatteryDevices.length > 0 && (
        <Panel className="mb-25">
          <Panel.Title>{t('alert.status.lowBatterySection.title')}</Panel.Title>
          <LowBatteryDeviceTable devices={lowBatteryDevices} />
        </Panel>
      )}

      <Panel>
        <Panel.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
          {t('alert.status.resolvedAlerts')}
          <Button onClick={() => setShowExportModal(true)}>{'Export'}</Button>
        </Panel.Title>
        {resolvedAlerts.length ? (
          <AlertTable alerts={resolvedAlerts.slice(0, 25)} deviceVerification={deviceVerification} />
        ) : (
          <AlertComp type="message">{t('alert.status.noAlerts.resolvedAlerts')}</AlertComp>
        )}
      </Panel>
      {showExportModal && <ExportModal exportHandler={exportAlerts} onClose={() => setShowExportModal(false)} />}
    </>
  );
});

export default AlertTableContainer;
